import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getServiceConfigoptionPrices } from '@/modules/Service/services'
import { ServiceInformationProps } from '@/modules/Service/types'
import ServiceTechnical from '../../../shared/ServiceTechnical'
import {
    createTransmuxingColumns,
    transformTransmuxingData,
    type TransmuxingRow,
} from './transmuxing-config'

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation()
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_CONFIGOPTIONS_PRICES,
            service?.service_id,
        ],
        queryFn: () =>
            getServiceConfigoptionPrices({ serviceID: service?.service_id }),
    })

    // Check if any configoption has price
    const hasPrice = useMemo(() => {
        return !!data?.some((configoption) => configoption.commitment_value > 0)
    }, [data])

    // Transform data to rows
    const rows = useMemo(() => {
        if (!service || !data) return []
        return transformTransmuxingData({ service, data })
    }, [service, data])

    // Create columns with conditional visibility
    const columns = useMemo(() => {
        const cols = createTransmuxingColumns(t, hasPrice)
        return cols.map((col) => ({
            ...col,
            hidden:
                col.key === 'service_stopped_date'
                    ? !service?.service_stopped_date
                    : col.hidden,
        }))
    }, [t, hasPrice, service?.service_stopped_date])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-4">
            <DataTableCustomized<TransmuxingRow>
                columns={columns}
                rows={rows}
                rowKey={(row) => String(row?.service_configoption_id)}
                size="small"
                resizable
                emptyMessage={t('no-data', { defaultValue: 'No data' })}
            />
            <ServiceTechnical service={service} />
        </div>
    )
}

export default Information
